import { useState, useEffect } from "react";
import { API_REGION_VERSION_MAP } from "../utils";
import axios from "axios";

type SEARCHGROUP = {
  UID: string;
  groupId: string;
  model: string;
  relationshipData: {
    role: string;
    status: string;
  };
  profile: {
    lastName: string;
    country: string;
    locale: string;
    email: string;
    zip: string;
    firstName: string;
  };
  memberSince: string;
  lastUpdated: string;
  memberSinceTimestamp: number;
  lastUpdatedTimestamp: number;
  permissions: string;
};

type SEARCHGROUPINFO = {
  groupId: string;
  model: string;
  groupData: {
    clinicName: string;
    priceGroup: string;
    country: string;
    city: string;
    SFHomeDeliveryOnly: boolean;
    industry: string;
    salesOrg: string;
    altID: string;
    payer: string;
    archived: boolean;
    salesZone: string;
    clinicType: string;
    customerClass: string;
    billTo: string;
    softClosed: boolean;
    state: string;
    billingZip: string;
    zip: string;
    companyCode: string;
    soldToClinicName: string;
    soldTo: string;
    customerGroup: string;
    customerPlanGroup: string;
    salesTerritory: string;
    accountType: string;
    approvedEmails: string;
    streetAddress: string;
    blockedClinic: boolean;
    paymentTerm: string;
    billingCity: string;
    shipTo: string;
    salesRegion: string;
    customerPriceProcedure: string;
    removedUsers?: string | string[];
  };
  created: string;
  lastUpdated: string;
  createdTimestamp: number;
  lastUpdatedTimestamp: number;
  membersCount: number;
};

const removeHyphen = (hyphenatedLocale: string) => {
  const region = hyphenatedLocale.split("-")[1].toUpperCase();
  return hyphenatedLocale.split("-")[0] + region;
};

export const useSearchClinics = (UID?: string) => {
  const [groupId, setGroupId] = useState<string | undefined>();
  const [loadingSearchClinicsResp, setLoadingSearchClinicsResp] =
    useState(true);
  const [searchClinicsRespError, setSearchClinicsRespError] = useState(false);
  const [searchClinicsResp, setSearchClinicsResp] = useState<
    Array<SEARCHGROUP> | []
  >([]);
  const [loadingSearchClinicsInfoResp, setLoadingSearchClinicsInfoResp] =
    useState(true);
  const [searchClinicsInfoRespError, setSearchClinicsInfoRespError] =
    useState(false);
  const [searchClinicsInfoResp, setSearchClinicsInfoResp] = useState<
    SEARCHGROUPINFO | undefined
  >();
  const apiVersion =
    API_REGION_VERSION_MAP[removeHyphen(process.env.REACT_APP_LOCALE!)];

  useEffect(() => {
    if (UID && process.env.REACT_APP_LOCALE !== "en-us") {
      window.gigya.accounts.getJWT({
        UID,
        callback: (resp) => {
          if (resp["errorCode"] === 0) {
            const headers = {
              "Content-Type": "application/json",
              Authorization: `Bearer ${resp.id_token}`,
            };
            const apiKey = process.env.REACT_APP_CDC_API_KEY ?? "";
            const SearchClinicsUrl = `${process.env.REACT_APP_CDC_API_BASEURL}/${process.env.REACT_APP_CDC_API_VERSION}/cdc/groups/searchClinics`;
            const SearchClinicsUrlTW = `${process.env.REACT_APP_CDC_API_BASEURL}/${apiVersion}/cdc/groups/searchClinics`;
            axios
              .post(
                process.env.REACT_APP_LOCALE !== "en-us"
                  ? SearchClinicsUrlTW
                  : SearchClinicsUrl,
                {
                  apiKey,
                  getAll: true,
                },
                { headers },
              )
              .then((res) => {
                setSearchClinicsResp(res.data.results);
                setLoadingSearchClinicsResp(false);
                setSearchClinicsRespError(false);
              })
              .catch((error) => {
                if (error.response) {
                  // The request was made and the server responded with a status code
                  // that falls out of the range of 2xx
                  console.log("Error Data: ", error.response.data);
                  console.log("Erorr Status: ", error.response.status);
                  console.log("Error Headers: ", error.response.headers);
                  setSearchClinicsResp([]);
                  setLoadingSearchClinicsResp(true);
                  setSearchClinicsRespError(true);
                } else if (error.request) {
                  // The request was made but no response was received
                  // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                  // http.ClientRequest in node.js
                  console.log(
                    "There was an error with your request: ",
                    error.request,
                  );
                  setSearchClinicsResp([]);
                  setLoadingSearchClinicsResp(true);
                  setSearchClinicsRespError(true);
                } else {
                  // Something happened in setting up the request that triggered an Error
                  console.log("Error: ", error.message);
                  setSearchClinicsResp([]);
                  setLoadingSearchClinicsResp(true);
                  setSearchClinicsRespError(true);
                }

                console.log("Error config: ", error.config);
                setSearchClinicsResp([]);
                setLoadingSearchClinicsResp(true);
                setSearchClinicsRespError(true);
              });
          }
        },
      });
    }

    return () => {
      setSearchClinicsResp([]);
      setLoadingSearchClinicsResp(true);
      setSearchClinicsRespError(false);
    };
  }, [UID]);

  useEffect(() => {
    if (UID && process.env.REACT_APP_LOCALE !== "en-us" && groupId) {
      window.gigya.accounts.getJWT({
        UID,
        callback: (resp) => {
          if (resp["errorCode"] === 0) {
            const headers = {
              "Content-Type": "application/json",
              Authorization: `Bearer ${resp.id_token}`,
            };
            const apiKey = process.env.REACT_APP_CDC_API_KEY ?? "";
            const SearchClinicsUrl = `${process.env.REACT_APP_CDC_API_BASEURL}/${process.env.REACT_APP_CDC_API_VERSION}/cdc/groups/searchClinics`;
            const SearchClinicsUrlTW = `${process.env.REACT_APP_CDC_API_BASEURL}/${apiVersion}/cdc/groups/searchClinics`;
            axios
              .post(
                process.env.REACT_APP_LOCALE !== "en-us"
                  ? SearchClinicsUrlTW
                  : SearchClinicsUrl,
                {
                  apiKey,
                  condition: "AND",
                  clinicID: [{ operator: "contains", lowerboundry: groupId }],
                },
                { headers },
              )
              .then((res) => {
                setSearchClinicsInfoResp(res.data.results[0]);
                setLoadingSearchClinicsInfoResp(false);
                setSearchClinicsInfoRespError(false);
              })
              .catch((error) => {
                if (error.response) {
                  // The request was made and the server responded with a status code
                  // that falls out of the range of 2xx
                  console.log("Info Error Data: ", error.response.data);
                  console.log("Info Erorr Status: ", error.response.status);
                  console.log("Info Error Headers: ", error.response.headers);
                } else if (error.request) {
                  // The request was made but no response was received
                  // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                  // http.ClientRequest in node.js
                  console.log(
                    "There was an error with your info request: ",
                    error.request,
                  );
                } else {
                  // Something happened in setting up the request that triggered an Error
                  console.log("Info Error: ", error.message);
                }

                console.log("Info Error config: ", error.config);
                setSearchClinicsInfoResp(undefined);
                setLoadingSearchClinicsInfoResp(true);
                setSearchClinicsInfoRespError(true);
              });
          }
        },
      });
    }

    return () => {
      setSearchClinicsInfoResp(undefined);
      setLoadingSearchClinicsInfoResp(true);
      setSearchClinicsInfoRespError(false);
      setGroupId(undefined);
    };
  }, [groupId]);

  return {
    searchClinicsResp,
    setSearchClinicsResp,
    searchClinicsInfoResp,
    setSearchClinicsInfoResp,
    setGroupId,
    loadingSearchClinicsInfoResp,
    loadingSearchClinicsResp,
    searchClinicsInfoRespError,
    searchClinicsRespError,
  };
};
